import React from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowRight, CarFront, ClipboardList, Search, KeyRound } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/contexts/LanguageContext";
import { useSite } from "@/contexts/SiteThemeContext";

const STEP_ICONS = [ClipboardList, Search, CarFront, KeyRound];

export default function ProcessSteps({ id = "process", compact = false, showCta = true }) {
  const site = useSite();
  const { lang } = useLanguage();
  const { siteKey } = useParams();
  const isFr = lang === "fr";

  const steps = isFr
    ? [
        { t: "Parlez-nous de votre situation", d: "Quelques questions rapides nous permettent de mieux comprendre vos besoins." },
        { t: "Nous vérifions vos options", d: "Votre situation est analysée afin d'identifier les solutions de financement qui pourraient vous convenir." },
        { t: "Trouvez le bon véhicule", d: "Nous vous aidons à trouver un véhicule compatible avec votre budget et votre financement." },
        { t: "Prenez la route", d: "Notre équipe vous accompagne dans les prochaines étapes jusqu'à la livraison." },
      ]
    : [
        { t: "Tell us about your situation", d: "A few quick questions help us understand your needs." },
        { t: "We check your options", d: "Your situation is reviewed to identify financing solutions that may fit." },
        { t: "Find the right vehicle", d: "We help you find a vehicle that matches your budget and financing." },
        { t: "Take the road", d: "Our team supports you through the next steps until delivery." },
      ];

  return (
    <section id={id} className={`mx-auto max-w-6xl px-4 ${compact ? "py-10" : "py-16"} sm:px-6 lg:px-8`} data-testid="process-steps">
      <div className="mb-8 max-w-2xl">
        <div className="text-[10px] font-bold uppercase tracking-[0.22em]" style={{ color: site.theme.primary }}>
          {isFr ? "Comment ça fonctionne" : "How it works"}
        </div>
        <h2 className="mt-2 text-3xl font-black tracking-tight sm:text-4xl" style={{ color: site.theme.text }}>
          {isFr ? "Simple du début à la fin" : "Simple from start to finish"}
        </h2>
      </div>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {steps.map((step, index) => {
          const Icon = STEP_ICONS[index];
          return (
            <div
              key={step.t}
              className="border p-5"
              style={{ backgroundColor: site.theme.surface, borderColor: site.theme.border, borderRadius: site.theme.radius }}
              data-testid={`process-step-${index + 1}`}
            >
              <div className="flex items-center justify-between">
                <span className="text-xs font-black tracking-[0.2em]" style={{ color: site.theme.accentWarm }}>
                  {String(index + 1).padStart(2, "0")}
                </span>
                <Icon className="h-5 w-5" style={{ color: site.theme.primary }} />
              </div>
              <h3 className="mt-4 text-lg font-black leading-tight" style={{ color: site.theme.text }}>{step.t}</h3>
              <p className="mt-2 text-sm leading-relaxed" style={{ color: site.theme.muted }}>{step.d}</p>
            </div>
          );
        })}
      </div>
      {showCta ? (
        <div className="mt-8 text-center">
          <Link to={`/${siteKey}/apply`}>
            <Button
              size="lg"
              className="font-bold uppercase tracking-wider text-white shadow-lg transition hover:-translate-y-0.5"
              style={{ backgroundColor: site.theme.accentWarm, borderRadius: site.theme.radius }}
              data-testid="process-cta"
            >
              {isFr ? "Vérifier mon admissibilité" : "Check my eligibility"} <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
          <p className="mt-3 text-xs font-semibold" style={{ color: site.theme.muted }}>
            {isFr ? "Rapide • Sans obligation • 100 % confidentiel" : "Fast • No obligation • 100% confidential"}
          </p>
        </div>
      ) : null}
    </section>
  );
}
